import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import type { Card as StudyCard, Deck, DeckVisibility } from '@study-decks/shared'

import { useAuth } from '../../auth/AuthContext.js'
import { Button, Card, Input } from '../../components'
import { showToast } from '../../components/Toast'
import {
  DeckApiError,
  createCard,
  deleteCard,
  getDeck,
  listCards,
  reorderCards,
  updateCard,
  updateDeck,
} from '../../lib/decks.js'
import styles from './StudentDeckEditorPage.module.css'

type SaveState = 'idle' | 'saving' | 'saved' | 'offline' | 'error'

interface PendingCard {
  term: string
  definition: string
}

interface EditorState {
  deck: Deck | null
  cards: StudyCard[]
  pendingTitle: string
  pendingDescription: string
  pendingVisibility: DeckVisibility
  pendingCards: Record<string, PendingCard>
  saveState: SaveState
  saveError: string | null
  loading: boolean
  errorMessage: string | null
}

const INITIAL_STATE: EditorState = {
  deck: null,
  cards: [],
  pendingTitle: '',
  pendingDescription: '',
  pendingVisibility: 'private',
  pendingCards: {},
  saveState: 'idle',
  saveError: null,
  loading: true,
  errorMessage: null,
}

function toPendingCards(cards: StudyCard[]): Record<string, PendingCard> {
  return Object.fromEntries(
    cards.map((card) => [card.id, { term: card.term, definition: card.definition }]),
  )
}

function errorText(err: unknown) {
  if (err instanceof DeckApiError || err instanceof Error) return err.message
  return 'Something went wrong — please try again.'
}

function saveStateText(saveState: SaveState, saveError: string | null) {
  if (saveState === 'saving') return 'Saving…'
  if (saveState === 'saved') return 'Saved just now'
  if (saveState === 'offline') return 'Offline — changes will save when you reconnect'
  if (saveState === 'error') return saveError ?? 'Save failed — retry'
  return 'All changes saved'
}

export default function StudentDeckEditorPage(): JSX.Element {
  const { deckId } = useParams()
  const { uid } = useAuth()
  const [state, setState] = useState<EditorState>(INITIAL_STATE)
  const addButtonRef = useRef<HTMLButtonElement | null>(null)
  const termRefs = useRef<Record<string, HTMLTextAreaElement | null>>({})
  const moveButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  const loadDeck = useCallback(async () => {
    if (!deckId) return
    setState((previous) => ({ ...previous, loading: true, errorMessage: null }))

    try {
      const [deck, cards] = await Promise.all([getDeck(deckId), listCards(deckId)])
      if (!deck) {
        setState((previous) => ({
          ...previous,
          loading: false,
          errorMessage: 'This deck no longer exists.',
        }))
        return
      }
      setState({
        deck,
        cards,
        pendingTitle: deck.title,
        pendingDescription: deck.description,
        pendingVisibility: deck.visibility === 'class' ? 'private' : deck.visibility,
        pendingCards: toPendingCards(cards),
        saveState: 'idle',
        saveError: null,
        loading: false,
        errorMessage: null,
      })
    } catch (err) {
      const message = errorText(err)
      setState((previous) => ({ ...previous, loading: false, errorMessage: message }))
      showToast(message, 'error')
    }
  }, [deckId])

  useEffect(() => {
    void loadDeck()
  }, [loadDeck])

  const dirtyDeckPatch = useMemo(() => {
    if (!state.deck) return null
    const patch: Partial<Pick<Deck, 'title' | 'description' | 'visibility'>> = {}
    if (state.pendingTitle !== state.deck.title) patch.title = state.pendingTitle
    if (state.pendingDescription !== state.deck.description) {
      patch.description = state.pendingDescription
    }
    if (state.pendingVisibility !== state.deck.visibility)
      patch.visibility = state.pendingVisibility
    return Object.keys(patch).length > 0 ? patch : null
  }, [state.deck, state.pendingDescription, state.pendingTitle, state.pendingVisibility])

  const saveDeckFields = useCallback(async () => {
    if (!deckId || !state.deck || !dirtyDeckPatch) return
    if (!navigator.onLine) {
      setState((previous) => ({ ...previous, saveState: 'offline' }))
      return
    }

    setState((previous) => ({ ...previous, saveState: 'saving', saveError: null }))
    try {
      await updateDeck(deckId, dirtyDeckPatch)
      const updatedAt = new Date().toISOString()
      setState((previous) => ({
        ...previous,
        deck: previous.deck ? { ...previous.deck, ...dirtyDeckPatch, updatedAt } : previous.deck,
        saveState: 'saved',
      }))
    } catch (err) {
      const message = errorText(err)
      setState((previous) => ({ ...previous, saveState: 'error', saveError: message }))
      showToast(message, 'error')
    }
  }, [deckId, dirtyDeckPatch, state.deck])

  const saveDirtyCards = useCallback(async () => {
    if (!deckId) return
    const dirtyCards = state.cards.filter((card) => {
      const pending = state.pendingCards[card.id]
      return pending && (pending.term !== card.term || pending.definition !== card.definition)
    })
    if (dirtyCards.length === 0) return
    if (!navigator.onLine) {
      setState((previous) => ({ ...previous, saveState: 'offline' }))
      return
    }

    setState((previous) => ({ ...previous, saveState: 'saving', saveError: null }))
    try {
      await Promise.all(
        dirtyCards.map((card) => {
          const pending = state.pendingCards[card.id]!
          return updateCard(deckId, card.id, pending)
        }),
      )
      setState((previous) => ({
        ...previous,
        cards: previous.cards.map((card) => {
          const pending = previous.pendingCards[card.id]
          return pending ? { ...card, ...pending } : card
        }),
        saveState: 'saved',
      }))
    } catch (err) {
      const message = errorText(err)
      setState((previous) => ({ ...previous, saveState: 'error', saveError: message }))
      showToast(message, 'error')
    }
  }, [deckId, state.cards, state.pendingCards])

  useEffect(() => {
    if (!dirtyDeckPatch) return
    const timer = window.setTimeout(() => {
      void saveDeckFields()
    }, 800)
    return () => window.clearTimeout(timer)
  }, [dirtyDeckPatch, saveDeckFields])

  useEffect(() => {
    const hasDirtyCard = state.cards.some((card) => {
      const pending = state.pendingCards[card.id]
      return pending && (pending.term !== card.term || pending.definition !== card.definition)
    })
    if (!hasDirtyCard) return
    const timer = window.setTimeout(() => {
      void saveDirtyCards()
    }, 800)
    return () => window.clearTimeout(timer)
  }, [saveDirtyCards, state.cards, state.pendingCards])

  useEffect(() => {
    if (state.saveState !== 'saved') return
    const timer = window.setTimeout(() => {
      setState((previous) =>
        previous.saveState === 'saved' ? { ...previous, saveState: 'idle' } : previous,
      )
    }, 3000)
    return () => window.clearTimeout(timer)
  }, [state.saveState])

  function updatePendingCard(cardId: string, patch: PendingCard) {
    setState((previous) => ({
      ...previous,
      pendingCards: { ...previous.pendingCards, [cardId]: patch },
    }))
  }

  async function handleAddCard() {
    if (!deckId || state.cards.length >= 490) return
    try {
      const card = await createCard(deckId, { term: '', definition: '' }, state.cards.length)
      setState((previous) => ({
        ...previous,
        deck: previous.deck
          ? {
              ...previous.deck,
              cardCount: previous.cards.length + 1,
              updatedAt: new Date().toISOString(),
            }
          : previous.deck,
        cards: [...previous.cards, card],
        pendingCards: {
          ...previous.pendingCards,
          [card.id]: { term: card.term, definition: card.definition },
        },
        saveState: 'saved',
      }))
      window.requestAnimationFrame(() => {
        termRefs.current[card.id]?.focus()
        termRefs.current[card.id]?.scrollIntoView({ block: 'center' })
      })
    } catch (err) {
      const message = errorText(err)
      setState((previous) => ({ ...previous, saveState: 'error', saveError: message }))
      showToast(message, 'error')
    }
  }

  async function handleDeleteCard(cardId: string) {
    if (!deckId || !window.confirm('Delete this card?')) return
    const cardIndex = state.cards.findIndex((card) => card.id === cardId)
    try {
      await deleteCard(deckId, cardId, state.cards.length)
      setState((previous) => {
        const cards = previous.cards.filter((card) => card.id !== cardId)
        const pendingCards = { ...previous.pendingCards }
        delete pendingCards[cardId]
        return {
          ...previous,
          deck: previous.deck
            ? { ...previous.deck, cardCount: cards.length, updatedAt: new Date().toISOString() }
            : previous.deck,
          cards,
          pendingCards,
          saveState: 'saved',
        }
      })
      window.requestAnimationFrame(() => {
        const nextCard = state.cards[cardIndex + 1] ?? state.cards[cardIndex - 1]
        if (nextCard && nextCard.id !== cardId) {
          termRefs.current[nextCard.id]?.focus()
        } else {
          addButtonRef.current?.focus()
        }
      })
    } catch (err) {
      const message = errorText(err)
      setState((previous) => ({ ...previous, saveState: 'error', saveError: message }))
      showToast(message, 'error')
    }
  }

  async function handleMoveCard(cardId: string, direction: -1 | 1) {
    if (!deckId) return
    const index = state.cards.findIndex((card) => card.id === cardId)
    const nextIndex = index + direction
    if (index < 0 || nextIndex < 0 || nextIndex >= state.cards.length) return

    const reordered = [...state.cards]
    const [moved] = reordered.splice(index, 1)
    if (!moved) return
    reordered.splice(nextIndex, 0, moved)
    const positioned = reordered.map((card, position) => ({ ...card, position: position + 1 }))

    setState((previous) => ({ ...previous, cards: positioned, saveState: 'saving' }))
    try {
      await reorderCards(
        deckId,
        positioned.map((card) => card.id),
      )
      setState((previous) => ({
        ...previous,
        cards: positioned,
        deck: previous.deck ? { ...previous.deck, updatedAt: new Date().toISOString() } : null,
        saveState: 'saved',
      }))
      window.requestAnimationFrame(() => {
        moveButtonRefs.current[`${cardId}:${direction === -1 ? 'up' : 'down'}`]?.focus()
      })
    } catch (err) {
      const message = errorText(err)
      setState((previous) => ({ ...previous, saveState: 'error', saveError: message }))
      showToast(message, 'error')
      void loadDeck()
    }
  }

  function handleRetrySave() {
    void saveDeckFields()
    void saveDirtyCards()
  }

  const addDisabled = state.cards.length >= 490

  if (!uid) return <></>

  if (state.loading) {
    return (
      <section className={styles.page}>
        <p role="status" aria-live="polite">
          Loading deck…
        </p>
      </section>
    )
  }

  if (state.errorMessage || !state.deck) {
    return (
      <section className={styles.page}>
        <div className={styles.errorBlock}>
          <p role="alert">{state.errorMessage ?? 'This deck no longer exists.'}</p>
          <Button type="button" variant="secondary" onClick={() => void loadDeck()}>
            Retry
          </Button>
          <Link to="/student/my-decks">Back to my decks</Link>
        </div>
      </section>
    )
  }

  return (
    <section className={styles.page}>
      <div className={styles.header}>
        <div>
          <p className={styles.eyebrow}>My deck</p>
          <h2>{state.deck.title || 'Untitled deck'}</h2>
        </div>
        <Link className={styles.backLink} to="/student/my-decks">
          Back to my decks
        </Link>
      </div>

      <Card className={styles.editorCard} elevated>
        <div className={styles.formGrid}>
          <Input
            label="Title"
            value={state.pendingTitle}
            onChange={(event) =>
              setState((previous) => ({ ...previous, pendingTitle: event.target.value }))
            }
          />
          <label className={styles.textareaField}>
            <span>Description</span>
            <textarea
              rows={3}
              value={state.pendingDescription}
              onChange={(event) =>
                setState((previous) => ({
                  ...previous,
                  pendingDescription: event.target.value,
                }))
              }
            />
          </label>
          <fieldset className={styles.visibilityGroup}>
            <legend>Visibility</legend>
            <label>
              <input
                type="radio"
                name="visibility"
                value="private"
                checked={state.pendingVisibility === 'private'}
                onChange={() =>
                  setState((previous) => ({ ...previous, pendingVisibility: 'private' }))
                }
              />
              Private
            </label>
            <label>
              <input
                type="radio"
                name="visibility"
                value="domain"
                checked={state.pendingVisibility === 'domain'}
                onChange={() =>
                  setState((previous) => ({ ...previous, pendingVisibility: 'domain' }))
                }
              />
              Domain (visible to everyone in your school)
            </label>
          </fieldset>
        </div>
        <div className={styles.saveRow}>
          <p role="status" aria-live="polite" aria-atomic="true" className={styles.saveState}>
            {state.saveState === 'saved' ? <span aria-hidden="true">✓ </span> : null}
            {saveStateText(state.saveState, state.saveError)}
          </p>
          {state.saveState === 'error' ? (
            <Button type="button" variant="secondary" size="sm" onClick={handleRetrySave}>
              Retry
            </Button>
          ) : null}
        </div>
      </Card>

      <div className={styles.cardsHeader}>
        <div>
          <h3>Cards</h3>
          <p className={styles.muted}>
            {state.cards.length} card{state.cards.length === 1 ? '' : 's'}
          </p>
        </div>
      </div>

      <ol className={styles.cardList}>
        {state.cards.map((card, index) => {
          const pending = state.pendingCards[card.id] ?? {
            term: card.term,
            definition: card.definition,
          }
          return (
            <li key={card.id} className={styles.cardItem}>
              <span className={styles.cardNumber} aria-hidden="true">
                {index + 1}.
              </span>
              <div className={styles.reorderControls}>
                <button
                  type="button"
                  ref={(node) => {
                    moveButtonRefs.current[`${card.id}:up`] = node
                  }}
                  className={styles.iconButton}
                  aria-label={`Move card ${index + 1} up`}
                  disabled={index === 0}
                  onClick={() => void handleMoveCard(card.id, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  ref={(node) => {
                    moveButtonRefs.current[`${card.id}:down`] = node
                  }}
                  className={styles.iconButton}
                  aria-label={`Move card ${index + 1} down`}
                  disabled={index === state.cards.length - 1}
                  onClick={() => void handleMoveCard(card.id, 1)}
                >
                  ↓
                </button>
              </div>
              <div className={styles.cardFields}>
                <label className={styles.textareaField}>
                  <span>Term</span>
                  <textarea
                    ref={(node) => {
                      termRefs.current[card.id] = node
                    }}
                    rows={2}
                    value={pending.term}
                    onChange={(event) =>
                      updatePendingCard(card.id, { ...pending, term: event.target.value })
                    }
                  />
                </label>
                <label className={styles.textareaField}>
                  <span>Definition</span>
                  <textarea
                    rows={3}
                    value={pending.definition}
                    onChange={(event) =>
                      updatePendingCard(card.id, { ...pending, definition: event.target.value })
                    }
                  />
                </label>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void handleDeleteCard(card.id)}
              >
                Delete card
              </Button>
            </li>
          )
        })}
      </ol>

      <div className={styles.addCardRow}>
        <Button
          ref={addButtonRef}
          type="button"
          variant="primary"
          onClick={() => void handleAddCard()}
          disabled={addDisabled}
        >
          Add card
        </Button>
        {addDisabled ? <p className={styles.muted}>Decks are capped at 490 cards.</p> : null}
      </div>
    </section>
  )
}
